import { useClasses } from "../../hooks/useClasses";
import React, { useState, useEffect } from "react";
import { FiPlus, FiSearch, FiMessageSquare, FiLink, FiTrash2 } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";
import { format, isPast } from "date-fns";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };





const SUBJECTS = ["Mathematics","Science","English","Nepali","Social Studies","Computer","Health","Optional Math"];
const GRADES   = ["A+","A","A-","B+","B","B-","C+","C","D","F"];

export default function TeacherHomework() {
  const { userData } = useAuth();
  const { classes } = useClasses();
  const { getHomework, addHomework, deleteHomework, getSubmissions, updateSubmission } = useData();

  const [hw,   setHw]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState("all");

  const [showAdd,      setShowAdd]      = useState(false);
  const [showSubs,     setShowSubs]     = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedHw,   setSelectedHw]  = useState(null);
  const [selectedSub,  setSelectedSub] = useState(null);
  const [subs,         setSubs]        = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ title:"", subject:"", classId:"", description:"", deadline:"" });
  const [fb,   setFb]   = useState({ feedback:"", grade:"" });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    const data = await getHomework({ teacherId: userData?.uid });
    setHw(data); setLoading(false);
  };

  const filtered = hw.filter(h => {
    const m = h.title.toLowerCase().includes(search.toLowerCase()) || h.subject.toLowerCase().includes(search.toLowerCase());
    if (filter === "active")  return m && !isPast(toDate(h.deadline));
    if (filter === "expired") return m && isPast(toDate(h.deadline));
    return m;
  });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title || !form.subject || !form.classId || !form.deadline) { toast.error("Fill all required fields"); return; }
    setSaving(true);
    try {
      await addHomework({ ...form, className: classes.find(x=>x.id===form.classId) ? `${classes.find(x=>x.id===form.classId).name} - ${classes.find(x=>x.id===form.classId).section}` : "", teacherId: userData?.uid, teacherName: userData?.name, totalStudents: classes.find(x=>x.id===form.classId)?.students || 35, deadline: toDate(form.deadline).toISOString() });
      toast.success("Homework assigned!");
      setShowAdd(false); setForm({ title:"", subject:"", classId:"", description:"", deadline:"" });
      load();
    } catch { toast.error("Failed to assign"); }
    finally { setSaving(false); }
  };

  const viewSubs = async (h) => {
    setSelectedHw(h);
    const s = await getSubmissions({ homeworkId: h.id });
    setSubs(s); setShowSubs(true);
  };

  const saveFeedback = async (e) => {
    e.preventDefault();
    if (!fb.feedback) { toast.error("Enter feedback"); return; }
    setSaving(true);
    try {
      await updateSubmission(selectedSub.id, { ...fb, status: "graded" });
      toast.success("Feedback saved!");
      setShowFeedback(false); setFb({ feedback:"", grade:"" });
      const s = await getSubmissions({ homeworkId: selectedHw.id });
      setSubs(s);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const delHw = async (id) => {
    if (!window.confirm("Delete this homework?")) return;
    await deleteHomework(id); toast.success("Deleted!"); load();
  };

  return (
    <div className="fade-in">
      <div className="page-head">
        <div><h1 className="page-title">📚 Homework Management</h1><p className="page-subtitle">{hw.length} assignments assigned</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus size={15} /> Assign Homework</button>
      </div>

      {/* Filter + Search */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <div className="search-box" style={{ flex: 1 }}>
          <FiSearch className="search-icon" size={14} />
          <input placeholder="Search homework…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {["all","active","expired"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "7px 16px", borderRadius: "var(--r6)",
            border: `1.5px solid ${filter===f?"var(--blue-700)":"var(--border)"}`,
            background: filter===f?"var(--blue-700)":"#fff",
            color: filter===f?"#fff":"var(--gray-500)",
            fontWeight: 600, fontSize: ".82rem", cursor: "pointer", fontFamily: "Inter,sans-serif",
          }}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>
        ))}
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div> : (
        <div className="card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Title</th><th>Class</th><th>Subject</th><th>Deadline</th><th>Submissions</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(h => {
                  const pct   = h.totalStudents > 0 ? Math.round((h.submissionsCount/h.totalStudents)*100) : 0;
                  const over  = isPast(toDate(h.deadline));
                  return (
                    <tr key={h.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{h.title}</div>
                        <div style={{ fontSize: ".74rem", color: "var(--gray-400)" }}>{h.description?.slice(0, 50)}…</div>
                      </td>
                      <td style={{ fontSize: ".84rem" }}>{h.className}</td>
                      <td><span className="badge badge-blue" style={{ fontSize: ".68rem" }}>{h.subject}</span></td>
                      <td style={{ fontSize: ".82rem", color: over ? "var(--red-600)" : "var(--gray-700)" }}>
                        {format(toDate(h.deadline), "MMM d, yyyy")}
                      </td>
                      <td>
                        <div style={{ fontSize: ".82rem", marginBottom: 4 }}>{h.submissionsCount||0} / {h.totalStudents||0}</div>
                        <div className="progress" style={{ width: 80 }}>
                          <div className={`progress-bar ${pct>=70?"green":pct>=40?"amber":"red"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${over?"badge-red":"badge-green"}`} style={{ fontSize: ".68rem" }}>
                          {over?"Expired":"Active"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => viewSubs(h)}>
                            <FiMessageSquare size={12} /> Submissions
                          </button>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => delHw(h.id)}>
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="empty-state"><div className="empty-icon">📚</div><div className="empty-title">No homework found</div></div>}
          </div>
        </div>
      )}

      {/* Add modal */}
      <Modal show={showAdd} onClose={() => setShowAdd(false)} title="Assign New Homework"
        footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving?<><div className="spinner spinner-sm"/>…</>:"Assign"}</button></>}>
        <div className="g g2">
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Title *</label>
            <input className="form-control" placeholder="Assignment title" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Subject *</label>
            <select className="form-control" value={form.subject} onChange={e=>setForm(p=>({...p,subject:e.target.value}))}>
              <option value="">Select</option>{SUBJECTS.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Class *</label>
            <select className="form-control" value={form.classId} onChange={e=>setForm(p=>({...p,classId:e.target.value}))}>
            </select>
          </div>
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Instructions</label>
            <textarea className="form-control" rows={3} placeholder="Detailed instructions…" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Deadline *</label>
            <input type="datetime-local" className="form-control" value={form.deadline} min={new Date().toISOString().slice(0,16)} onChange={e=>setForm(p=>({...p,deadline:e.target.value}))} />
          </div>
        </div>
      </Modal>

      {/* Submissions modal */}
      <Modal show={showSubs} onClose={() => setShowSubs(false)} title={`Submissions — ${selectedHw?.title}`} size="lg">
        <div style={{ marginBottom: 10 }}><span className="badge badge-blue">{subs.length} submissions</span></div>
        {subs.length === 0 ? <div className="empty-state"><div className="empty-icon">📬</div><div className="empty-title">No submissions yet</div></div> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Student</th><th>Submitted</th><th>Notes</th><th>Link</th><th>Grade</th><th>Action</th></tr></thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.studentName}</td>
                    <td style={{ fontSize: ".8rem", color: "var(--gray-500)" }}>{s.submittedAt ? format(toDate(s.submittedAt), "MMM d, HH:mm") : "—"}</td>
                    <td style={{ maxWidth: 160 }}><div style={{ fontSize: ".8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.notes || "—"}</div></td>
                    <td>{s.fileUrl ? <a href={s.fileUrl} target="_blank" rel="noreferrer" style={{ color: "var(--blue-700)", display:"flex",alignItems:"center",gap:4,fontSize:".8rem" }}><FiLink size={11}/>Open</a> : "—"}</td>
                    <td style={{ fontWeight: 700, color: "var(--green-600)" }}>{s.grade || "—"}</td>
                    <td><button className="btn btn-secondary btn-sm" onClick={()=>{setSelectedSub(s);setFb({feedback:s.feedback||"",grade:s.grade||""});setShowFeedback(true);}}>Feedback</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Feedback modal */}
      <Modal show={showFeedback} onClose={() => setShowFeedback(false)} title="Give Feedback"
        footer={<><button className="btn btn-ghost" onClick={() => setShowFeedback(false)}>Cancel</button><button className="btn btn-primary" onClick={saveFeedback} disabled={saving}>{saving?<><div className="spinner spinner-sm"/>…</>:"Save"}</button></>}>
        <div className="form-group">
          <label className="form-label">Grade</label>
          <select className="form-control" value={fb.grade} onChange={e=>setFb(p=>({...p,grade:e.target.value}))}>
            <option value="">Select grade</option>{GRADES.map(g=><option key={g}>{g}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Feedback *</label>
          <textarea className="form-control" rows={4} placeholder="Write your feedback…" value={fb.feedback} onChange={e=>setFb(p=>({...p,feedback:e.target.value}))} />
        </div>
        {selectedSub?.fileUrl && <a href={selectedSub.fileUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><FiLink size={12}/>View Submission Link</a>}
      </Modal>
    </div>
  );
}
